import { isEnLang } from '@/utils/lang';
import i18n from '@/plugins/i18n';

const UNIT_KEYS = {
  wan: 'selfSelected.tip.formatter_unit_wan',
  million: 'selfSelected.tip.formatter_unit_million',
  yi: 'selfSelected.tip.formatter_unit_yi',
  k: 'selfSelected.tip.formatter_unit_k',
  m: 'selfSelected.tip.formatter_unit_m',
  b: 'selfSelected.tip.formatter_unit_b'
};
const UNIT_FALLBACK = { wan: '万', million: '百万', yi: '亿', k: 'K', m: 'M', b: 'B' };

function getUnit(key) {
  const t = i18n?.global?.t;
  return (typeof t === 'function' ? t(UNIT_KEYS[key]) : null) ?? UNIT_FALLBACK[key];
}

/**
 * 与 formatBigPriceByLocale 相同规则，拆成「展示数字」与「单位」（便于模板分开展示）
 * @param {number|string|null|undefined} value 原始金额等数值（如元）
 * @returns {{ displayValue: string, displayUnit: string }}
 */
export function splitBigPriceByLocale(value) {
  const num = Number(value);
  if (!num && num !== 0) {
    return { displayValue: '', displayUnit: '' };
  }
  const useEn = isEnLang();

  if (useEn) {
    if (Math.abs(num) >= 1e9) {
      return { displayValue: (num / 1e9).toFixed(2), displayUnit: getUnit('b') };
    }
    if (Math.abs(num) >= 1e6) {
      return { displayValue: (num / 1e6).toFixed(2), displayUnit: getUnit('m') };
    }
    if (Math.abs(num) >= 1e3) {
      return { displayValue: (num / 1e3).toFixed(2), displayUnit: getUnit('k') };
    }
    return { displayValue: num.toFixed(0), displayUnit: '' };
  }

  if (Math.abs(num) >= 1e8) {
    return { displayValue: (num / 1e8).toFixed(2), displayUnit: getUnit('yi') };
  }
  if (Math.abs(num) >= 1e6) {
    return { displayValue: (num / 1e6).toFixed(2), displayUnit: getUnit('million') };
  }
  if (Math.abs(num) >= 1e4) {
    return { displayValue: (num / 1e4).toFixed(2), displayUnit: getUnit('wan') };
  }
  return { displayValue: num.toFixed(0), displayUnit: '' };
}

/**
 * 按语言格式化大数单位（中文：万/亿，英文：K/M/B）
 * @param {number} value
 * @returns {string}
 */
export function formatBigPriceByLocale(value) {
  const { displayValue, displayUnit } = splitBigPriceByLocale(value);
  return displayValue ? displayValue + displayUnit : '';
}

/**
 * 按语言返回大数单位（仅单位，不包含数值）
 * 中文：亿/百万/万，英文：B/M/K
 * @param {number} value
 * @returns {string}
 */
export function formatValueUnitByLocale(value) {
  const absValue = Math.abs(Number(value));
  if (!absValue) return '';
  if (isEnLang()) {
    return absValue >= 1e9 ? getUnit('b') : absValue >= 1e6 ? getUnit('m') : absValue >= 1e3 ? getUnit('k') : '';
  }
  return absValue >= 1e8 ? getUnit('yi') : absValue >= 1e6 ? getUnit('million') : absValue >= 1e4 ? getUnit('wan') : '';
}

/**
 * 按语言格式化总量（中文：万/亿，英文：K/M/B）
 * @param {number} total
 * @param {number} [fixed=1]
 * @param {boolean} [thousandFormat=false] 千分位
 * @returns {string}
 */
export function formatAllTotalByLocale(total, fixed = 1, thousandFormat = false) {
  const num = Number(total);
  if (typeof num !== 'number' || isNaN(num)) return '';
  const useEn = isEnLang();

  const formatVal = (val, f) => {
    const str = (f === 0 ? Math.floor(val) : val).toFixed(f);
    return thousandFormat ? str.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : str;
  };

  if (useEn) {
    if (Math.abs(num) >= 1e9) return formatVal(num / 1e9, num % 1e9 === 0 ? 0 : fixed) + getUnit('b');
    if (Math.abs(num) >= 1e6) return formatVal(num / 1e6, num % 1e6 === 0 ? 0 : fixed) + getUnit('m');
    if (Math.abs(num) >= 1e3) return formatVal(num / 1e3, num % 1e3 === 0 ? 0 : fixed) + getUnit('k');
    return formatVal(num, num % 1 === 0 ? 0 : fixed);
  }

  if (Math.abs(num) >= 1e8) return formatVal(num / 1e8, num % 1e8 === 0 ? 0 : fixed) + getUnit('yi');
  if (Math.abs(num) >= 1e4) return formatVal(num / 1e4, num % 1e4 === 0 ? 0 : fixed) + getUnit('wan');
  return formatVal(num, num % 1 === 0 ? 0 : fixed);
}

/**
 * 按语言格式化简短总量（中文：w/k，英文：K/M/B）
 * @param {number} total
 * @returns {string|number}
 */
export function formatTotalByLocale(total, digits = 0) {
  if (total === 0) return total;
  const num = Number(total);
  const useEn = isEnLang();

  if (useEn) {
    if (num >= 1e9) return (num % 1e9 === 0 ? num / 1e9 : toFixedNoRound(num / 1e9, 1)) + getUnit('b');
    if (num >= 1e6) return (num % 1e6 === 0 ? num / 1e6 : toFixedNoRound(num / 1e6, 1)) + getUnit('m');
    if (num >= 1e3) return (num % 1e3 === 0 ? num / 1e3 : toFixedNoRound(num / 1e3, 1)) + getUnit('k');
    return num.toFixed(digits);
  }

  if (num % 1e4 === 0) return num / 1e4 + 'w';
  if (num > 1e4) return toFixedNoRound(num / 1e4, 1) + 'w';
  if (num % 1e3 === 0) return num / 1e3 + 'k';
  if (num > 1e3) return toFixedNoRound(num / 1e3, 1) + 'k';
  return num.toFixed(digits);
}

function toFixedNoRound(num, digits) {
  const factor = 10 ** digits;
  return Math.floor(num * factor) / factor;
}

export function formatLargeNumber(number, largeNumber = 1000) {
  if (typeof number !== 'number') {
    if (process.env.NODE_ENV === 'development') {
      throw new Error('formatLargeNumber方法入参为number类型');
    } else {
      return number;
    }
  }
  if (number >= largeNumber) {
    return `${largeNumber - 1}+`;
  }
  return number;
}

export function formatNumberLimit(num, limit = 999) {
  if (num > limit) {
    return `${limit}+`;
  }
  return num;
}

/**
 * 格式化数字, 添加单位
 * 中文：万(w)/百万(m)/千(k)，英文：K/M/B（10^3/10^6/10^9）
 * @param {number} num - 要格式化的数字
 * @returns {string} - 格式化后的字符串
 */
export function formatNumberAddUnit(num) {
  if (typeof num !== 'number' || isNaN(num)) return num?.toString() ?? '';
  const absNum = Math.abs(num);
  const useEn = isEnLang();

  if (useEn) {
    if (absNum >= 1e9) return (num / 1e9).toFixed(2) + getUnit('b');
    if (absNum >= 1e6) return (num / 1e6).toFixed(2) + getUnit('m');
    if (absNum >= 1e3) return (num / 1e3).toFixed(2) + getUnit('k');
    return num.toString();
  }

  let value, unit;
  if (absNum >= 1e6) {
    value = num / 1e6;
    unit = 'm';
  } else if (absNum >= 1e4) {
    value = num / 1e4;
    unit = 'w';
    if (parseFloat(value.toFixed(2)) >= 100) {
      value = num / 1e6;
      unit = 'm';
    }
  } else {
    if (absNum < 1000) return num.toString();
    value = num / 1e3;
    unit = 'k';
    if (parseFloat(value.toFixed(2)) >= 10) {
      value = num / 1e4;
      unit = 'm';
    }
  }
  return value.toFixed(2) + unit;
}

/**
 * 国际化数值单位转换
 * @param {number} num - 要转换的数字
 * @param {string} targetLocale - 目标语言环境 ('zh-CN' 或其他)
 * @param {number} decimal - 小数位数
 * @returns {string} - 格式化后的字符串
 * @example
 * localizeNumber(20000, 'zh-CN') // "2万"
 * localizeNumber(200000000, 'zh-CN') // "2亿"
 * localizeNumber(2000000, 'en-US') // "2 million"
 * localizeNumber(2000000000, 'en-US') // "2 billion"
 */
export function localizeNumber(num, targetLocale = 'zh-CN', decimal = 2) {
  if (typeof num !== 'number' || isNaN(num)) {
    return num?.toString() || '';
  }

  const isChinese = targetLocale.toLowerCase() === 'zh-cn';

  const format = (value) => {
    // const formatOptions = {
    //   maximumFractionDigits: decimal
    // };
    // return value.toLocaleString(targetLocale, formatOptions); // 每3位加一个逗号。暂留
    return value.toFixed(decimal);
  };

  // 中文环境：使用万/亿单位（i18n）
  if (isChinese) {
    if (num >= 1000000000000) {
      const tFn = i18n?.global?.t;
      const trillion = typeof tFn === 'function' ? tFn('selfSelected.tip.formatter_unit_trillion') : null;
      return format(num / 1000000000000) + (trillion ?? '万亿');
    }
    if (num >= 100000000) return format(num / 100000000) + getUnit('yi');
    if (num >= 10000) return format(num / 10000) + getUnit('wan');
    return format(num);
  }

  // 英文环境：使用 K/M/B（i18n）
  if (num >= 1000000000) return format(num / 1000000000) + ' ' + getUnit('b');
  if (num >= 1000000) return format(num / 1000000) + ' ' + getUnit('m');
  if (num >= 1000) return format(num / 1000) + ' ' + getUnit('k');
  return format(num);
}

/**
 * 将毫秒转换为时分秒格式（xx小时 xx分钟 xx秒）
 * @param {number} ms - 毫秒数
 * @returns {string} - 时分秒格式字符串
 */
export function formatMsToHMS(ms) {
  if (!ms) return '--';

  const seconds = Math.floor(ms / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const result = [];
  if (hours) result.push(`${hours}小时`);
  if (minutes) result.push(`${minutes}分钟`);
  if (secs || !result.length) result.push(`${secs}秒`);

  return result.join(' ');
}

/**
 * 固定小数位；`null` / `undefined` 或无法转为有效数字时为 `--`
 * @param {number|string|null|undefined} value
 * @param {number} [fractionDigits=2]
 * @returns {string}
 */
export function formatFloatMetric(value, fractionDigits = 2) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  const n = Number(value);
  return Number(n.toFixed(fractionDigits)).toString();
}

/**
 * 百分比 `x.xx%`；无效为 `--`（不会出现 `--%`）
 * @param {number|string|null|undefined} value 已为百分点量级的数值
 * @returns {string}
 */
export function formatPercentMetric(value) {
  const s = formatFloatMetric(value, 2);
  return s === '--' ? '--' : `${s}%`;
}
